import {
  BranchTagConfig,
  DepartmentTagConfig,
  FilterTableResponse,
  ServiceTagConfig,
  TagBaseResponse,
  TagCreatePayload,
  TagDropdownItem,
  TagDropdownQuery,
  TagHierarchyItem,
  TagListItem,
  TagListQuery,
  TagUpdateRequest,
  TagWithMetadata,
  TagWithMetadataResponse,
} from "@/app/types/tags";
import { apiSlice } from "@/features/api/ApiSlice";

export const TagsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTags: builder.query<FilterTableResponse<TagListItem>, TagListQuery>({
      query: ({ org_id, event_ids, tag_type }) => {
        const params = new URLSearchParams()

        params.append("org_id", String(org_id))
        if (tag_type) params.append("tag_type", tag_type)
        event_ids?.forEach((id) => {
          params.append("event_ids", String(id))
        })

        return {
          url: `/tags/?${params.toString()}`,
          method: "GET",
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((tag) => ({ type: "Tags" as const, id: tag.tag_id })),
              { type: "Tags" as const, id: "LIST" },
            ]
          : [{ type: "Tags" as const, id: "LIST" }],
    }),

    getTagById: builder.query<TagWithMetadata, number>({
      query: (tag_id) => ({
        url: `/tags/${tag_id}`,
        method: "GET",
      }),
      transformResponse: (response: TagWithMetadataResponse): TagWithMetadata => {
        const metadata = response.metadata;
        let fixedReponse = {
          ...response,
          code: response.code ?? undefined,
          tag_status: response.status === "active",
          parent_tag_id: response.path.length > 1 ? response.path[response.path.length - 2] : null
        }

        if (response.type === "branch") {
          return {
            ...fixedReponse,
            type: "branch",
            metadata: metadata as BranchTagConfig,
          };
        }

        if (response.type === "department") {
          return {
            ...fixedReponse,
            type: "department",
            metadata: metadata as DepartmentTagConfig,
          };
        }

        return {
          ...fixedReponse,
          type: "service",
          metadata: metadata as ServiceTagConfig,
        };
      },
      providesTags: (_result, _error, tag_id) => [{ type: "Tags", id: tag_id }],
    }),

    getTagDropdown: builder.query<TagDropdownItem[], TagDropdownQuery>({
      query: ({ tag_types, org_id, event_id }) => {
        const params = new URLSearchParams();

        tag_types.forEach(type => {
          params.append("tag_types", type);
        });

        params.append("org_id", String(org_id));

        if (typeof event_id === "number") {
          params.append("event_id", String(event_id));
        }

        return {
          url: `/tags/dropdown?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [{ type: "Tags", id: "LIST" }],
    }),

    getTagHierarchy: builder.query<TagHierarchyItem[], number>({
      query: (tag_id) => ({
        url: `/tags/${tag_id}/hierarchy`,
        method: "GET",
      }),
      providesTags: (_result, _error, tag_id) => [{ type: "Tags", id: tag_id }],
    }),

    createTag: builder.mutation<TagBaseResponse, TagCreatePayload>({
      query: (payload) => ({
        url: "/tags/create",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "Tags", id: "LIST" }],
    }),

    updateTag: builder.mutation<TagBaseResponse, TagUpdateRequest>({
      query: ({ tag_id, ...payload }) => ({
        url: `/tags/${tag_id}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: (_result, _error, { tag_id }) => [
        { type: "Tags", id: "LIST" },
        { type: "Tags", id: tag_id },
      ],
    }),

    deleteTag: builder.mutation<TagBaseResponse, number>({
      query: (tag_id) => ({
        url: `/tags/${tag_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, tag_id) => [
        { type: "Tags", id: "LIST" },
        { type: "Tags", id: tag_id },
      ],
    }),
  }),
});

export const {
  useGetTagsQuery,
  useGetTagByIdQuery,
  useGetTagDropdownQuery,
  useGetTagHierarchyQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
} = TagsApi;
